/**
 * Shared Auth Session Handler Utilities
 *
 * Provides a centralized mechanism for coordinating auth failure handling
 * across multiple entry points (middleware, interceptor, visibility check).
 * This prevents race conditions and duplicate error messages when session expires.
 */

// Module-level flag to coordinate auth failure handling across the app
// This prevents multiple simultaneous logout attempts and error toasts
let authFailureInProgress = false

/**
 * Check if auth failure is currently being handled
 * @returns true if auth failure handling is in progress
 */
export function isAuthFailureInProgress(): boolean {
  return authFailureInProgress
}

/**
 * Set the auth failure in progress flag
 * @param value - true to mark as in progress, false to clear
 */
export function setAuthFailureInProgress(value: boolean): void {
  authFailureInProgress = value
}

/**
 * Reset the auth failure flag (for testing purposes)
 */
export function resetAuthFailureFlag(): void {
  authFailureInProgress = false
}

/**
 * Execute auth failure handling with automatic debouncing
 * 
 * @param handler - Async function to execute if no other failure is in progress
 * @returns Promise that resolves when handling completes, or immediately if already in progress
 */
export async function executeAuthFailureHandler(
  handler: () => Promise<void>
): Promise<void> {
  // If auth failure is already being handled, skip to prevent duplicate messages
  if (authFailureInProgress) {
    return
  }

  // Set the flag to prevent concurrent handling
  authFailureInProgress = true

  try {
    await handler()
  } finally {
    // Reset the flag after handler completes
    // Brief timeout to allow navigation/cleanup to complete
    setTimeout(() => {
      authFailureInProgress = false
    }, 500)
  }
}
